import { ApiError } from "./errors";

/**
 * What a 409 `conflict` actually means. The backend uses one code for several
 * situations, distinguished by message/details:
 * - "stale ETag" (If-Match mismatch), "stale <resource> version" or reason
 *   "stale_version": someone else changed the resource; reload before editing.
 * - reason "stale_manifest_revision": the server's manifest moved on; retry
 *   with a fresh revision (the resource being edited did not change).
 * - "... already exists": a unique field (name, email, slug) is taken.
 * - reason "sync_in_progress" / "... already in progress": try again later.
 * - "routing policy publish preflight failed" (details.preflight): the
 *   server-side activation gate rejected the route on some target node.
 * - capability "routing_policy_preflight" unavailable: the gate can't run.
 * - "enrollment token expired" / "enrollment token already consumed".
 * - "published package requires every external subscription ...": a source
 *   is disabled or has no usable synchronized inventory.
 * - details.resource "tenant_admin": the last active tenant admin can't be
 *   disabled or demoted.
 * - reason "upstream_package_changed": a tenant package must be refreshed from
 *   its upstream package before it can be assigned.
 * - anything else (e.g. blocked deletion with referenced_by).
 */
export type ConflictKind =
  | "stale"
  | "staleManifest"
  | "duplicate"
  | "inProgress"
  | "preflightFailed"
  | "preflightUnavailable"
  | "tokenExpired"
  | "tokenConsumed"
  | "externalUnusable"
  | "lastTenantAdmin"
  | "packageNeedsRefresh"
  | "other";

export function classifyConflict(error: unknown): ConflictKind | null {
  if (!(error instanceof ApiError) || error.code !== "conflict") return null;
  const reason = (error.details as { reason?: unknown } | undefined)?.reason;
  const message = (error.message || "").trim();
  const details = (error.details ?? {}) as { preflight?: unknown; capability?: unknown; resource?: unknown };
  // A server's manifest moved on (another node change): retry with a fresh
  // revision. Not a stale edit of the resource being saved.
  if (reason === "stale_manifest_revision") return "staleManifest";
  // External subscriptions report a stale revision (reason "stale_revision").
  if (reason === "stale_version" || reason === "stale_revision" || isStaleVersionMessage(message))
    return "stale";
  if (details.resource === "tenant_admin") return "lastTenantAdmin";
  if (reason === "upstream_package_changed") return "packageNeedsRefresh";
  if (details.preflight !== undefined || /publish preflight failed/i.test(message)) return "preflightFailed";
  if (details.capability === "routing_policy_preflight" || /preflight is unavailable/i.test(message))
    return "preflightUnavailable";
  if (/enrollment token expired/i.test(message)) return "tokenExpired";
  if (/enrollment token already consumed/i.test(message)) return "tokenConsumed";
  if (/requires every external subscription/i.test(message)) return "externalUnusable";
  if (
    reason === "sync_in_progress" ||
    reason === "idempotency_in_progress" ||
    /(already|still) in progress/i.test(message)
  )
    return "inProgress";
  if (/already exists/i.test(message)) return "duplicate";
  return "other";
}

/**
 * The backend's stale-version wording: "stale ETag" (If-Match mismatch),
 * "stale <resource> version", or the frontend's own "modified by someone else".
 * Deliberately not any message that merely contains "stale".
 */
export function isStaleVersionMessage(message: string): boolean {
  return (
    /^stale etag$/i.test(message) ||
    /^stale [a-z ]*version$/i.test(message) ||
    /^stale [a-z ]*revision$/i.test(message) ||
    /modified by someone else/i.test(message)
  );
}

/** The field a duplicate conflict is about, when the backend message names it. */
export function duplicateField(error: unknown): "email" | "slug" | "name" | null {
  if (classifyConflict(error) !== "duplicate" || !(error instanceof ApiError)) return null;
  const message = error.message.toLowerCase();
  if (message.includes("email")) return "email";
  if (message.includes("slug")) return "slug";
  if (message.includes("name")) return "name";
  return null;
}
